import type { PublicBatch, PublicSystemHealth } from "@homesite/api-contract";
import { queryKeys } from "@homesite/queries";
import { useQueries } from "@tanstack/react-query";
import { useApiClient } from "../lib/ApiClientContext";

export interface DashboardData {
	health: PublicSystemHealth | null;
	recentBatches: PublicBatch[];
	loading: boolean;
	error: Error | null;
}

/**
 * Composed dashboard hook — fetches system health and the most recent 5 batches
 * in parallel. Recent activity is derived from recent batches since no dedicated
 * activity endpoint exists.
 */
export function useDashboard(): DashboardData {
	const client = useApiClient();

	const results = useQueries({
		queries: [
			{
				queryKey: queryKeys.publicVerification.systemHealth,
				queryFn: async () => {
					const result = await client.publicVerification.getSystemHealth();
					if (result.status === 200) {
						return result.body;
					}
					throw new Error("Failed to fetch system health");
				},
			},
			{
				queryKey: queryKeys.publicVerification.batches.list(1, 5, undefined),
				queryFn: async () => {
					const result = await client.publicVerification.getBatches({
						query: { page: 1, limit: 5 },
					});
					if (result.status === 200) {
						return result.body;
					}
					throw new Error("Failed to fetch recent batches");
				},
			},
		],
	});

	const [healthQuery, batchesQuery] = results;

	const loading = results.some((r) => r.isLoading);
	const firstError = healthQuery.error ?? batchesQuery.error;

	return {
		health: (healthQuery.data as PublicSystemHealth | undefined) ?? null,
		recentBatches:
			(batchesQuery.data as { items: PublicBatch[] } | undefined)?.items ?? [],
		loading,
		error: firstError ?? null,
	};
}
